import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import Label from 'Components/Common/components/Label';
import { NEXT, RECATEGORIZE_TITLE, WARNING_MESSAGE } from '../utils/Constants';
import ModalContainer from 'Components/Common/Modal/ModalContainer';
import { WarningIcon } from 'assests/GmsIcons';
import Stepper from './Stepper';
import { getSteps } from '../utils/Helper';
import SelectTarget from '../SelectCategory/SelectTarget';
import SelectAttributes from '../SelectAttributes/SelectAttributes';
import Review from '../Review/Review';
import ModalHeader from 'Components/Common/Modal/ModalHeader';
import { ModalBody } from 'react-bootstrap';
import ModalFooter from 'Components/Common/Modal/ModalFooter';
import { ProductsContext } from '../../ProductsContext';
import { getRecategorizationList, PostRecategorization } from '../services/RecategorizeServices';
import AttributeValuesMapping from '../SelectAttributes/AttributeValuesMapping';
import AttributeValuesMappingHeader from '../SelectAttributes/AttributeValuesMappingHeader';
import { AppContext } from 'Context/AppContext';
import RecategorizationWrapper from '../Result/RecategorizationWrapper';
import { toast } from 'react-toastify';


type TRecategorizeModalProps = {
    showRecategorizePopup: boolean;
    modalRecategorizeClose: () => void;
    selectedProducts: TOption[],
    setSelectedProducts: Dispatch<SetStateAction<TOption[]>>
}

const RecategorizeModal = ({ showRecategorizePopup, modalRecategorizeClose, selectedProducts, setSelectedProducts }: TRecategorizeModalProps) => {
    const prodContext = useContext(ProductsContext);
    const appData = useContext(AppContext);
    const industryId = appData?.get_selected_industry()?.id;
    const [payload, setPayload] = useState<TRecategorizePayload>({
        industry_id: industryId,
        common_attr_list: [],
        product_list: [],
        source_category: prodContext?.selected_Category?.id ?? null,
        source_category_name: prodContext?.selected_Category?.name ?? null,
        target_category: null,
        target_category_name: null,
        unique_attr_source_list: []
    });
    const [finalPayload, setFinalPayload] = useState<TRecategorizePayload>({
        industry_id: industryId,
        common_attr_list: [],
        product_list: [],
        source_category: prodContext?.selected_Category?.id ?? null,
        source_category_name: prodContext?.selected_Category?.name ?? null,
        target_category: null,
        target_category_name: null,
        unique_attr_source_list: []
    });
    const [recategorizationResponse, setRecategorizationResponse] = useState<RecategorizationResponse>();
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [showResult, setShowResult] = useState<boolean>(false);
    const [attributeValueData, setAttributeValueData] = useState<AttributeValueMapping | null>(null);
    //Tab index for select attributes
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [loader, setLoader] = useState<boolean>(false)

    useEffect(() => {
        const productIds = selectedProducts.map(product => product.id);
        setPayload((payload) => {
            return {
                ...payload,
                product_list: productIds,
            }
        })
    }, []);

    useEffect(() => {
        if (payload?.target_category) {
            getRecategorizationData();
        }
    }, [payload?.target_category, selectedProducts]);

    const getRecategorizationData = async () => {
        try {
            const productIds = selectedProducts.map(product => product.id);
            const formattedProductIds = JSON.stringify(productIds);
            const response: RecategorizationResponse = await getRecategorizationList(payload?.source_category ?? '', payload?.target_category ?? '', formattedProductIds);
            setRecategorizationResponse(sortTargetAttributes(response))
            const commonAttrList: CommonAttributePayload[] = response.common_attr_list.map(attr => ({
                attribute_id: attr.attribute_id,
                attribute_name: attr.attribute_name,
                checked: true
            }));
            const uniqueAttrList: UniqueAttributePayload[] = response.unique_attr_source_list.map(attr => ({
                attribute_id: attr.attribute_id,
                attribute_name: attr.attribute_name,
                checked: false,
                action: null,
                common_attribute_values: attr?.attribute_value?.map(val => val.value)
            }));
            setPayload(prev => ({
                ...prev,
                product_list: productIds,
                common_attr_list: commonAttrList,
                unique_attr_source_list: uniqueAttrList,
            }));

        } catch (error) {
            console.log("ERROR", error);
        }
    };

    const sortTargetAttributes = (response: RecategorizationResponse): RecategorizationResponse => {
        if (!response.unique_attr_target_list?.length) return response

        return {
            ...response,
            unique_attr_target_list: [...response.unique_attr_target_list].sort((a, b) =>
                (a.attribute_name ?? '').localeCompare(b.attribute_name ?? '', undefined, {
                    sensitivity: 'base',
                    numeric: true
                })
            )
        }
    }

    const handleStepChange = (newStep: number) => {
        setCurrentStep(newStep);
    };

    const validateAttributeValue = (attrValue: UniqueAttributeValue): boolean => {
        if (!attrValue?.action) return false
        if (attrValue?.action === 'create') return true
        if (!attrValue?.target_attribute_value) return false
        return true
    }

    const validateUniqueAttribute = (uniqueAttr: UniqueAttributePayload): boolean => {
        if (!uniqueAttr?.checked) return true
        if (!uniqueAttr?.action) return false
        if (uniqueAttr?.action === 'transfer') return true
        if (!uniqueAttr?.target_attribute_id) return false
        return uniqueAttr?.unique_attribute_values?.every(validateAttributeValue) as boolean
    }

    const validations = (): boolean => {
        if (currentStep !== 2) return true
        const isValid = payload?.unique_attr_source_list?.every(validateUniqueAttribute) ?? false
        if (!isValid) {
            toast.warning("Please set the action/target attribute for selected attribute and its attribute values")
        }
        return isValid
    }

    const handleNext = () => {
        if (currentStep < getSteps(currentStep).length) {
            if (validations()) {
                handleStepChange(currentStep + 1);
            }
        }
    };
    const handleBack = () => {
        if (currentStep > 1) {
            handleStepChange(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        setLoader(true)
        try {
            const res = await PostRecategorization(finalPayload)
            if (res?.done)
                setShowResult(true)
            else toast.error(res?.message)
        } catch (error) {
            toast.error("Recategorization failed")
        } finally {
            setLoader(false)
        }
    }

    const disableNextButton = () => {
        if (currentStep === 1) { return payload?.target_category ? false : true }
        return currentStep === getSteps(currentStep).length
    }

    const handleAttributeValuesBackButton = () => {
        if (attributeValueData?.payload) setPayload(attributeValueData?.payload)
        resetAttributeValuesModal();
    }
    const handleDoneButton = () => {
        resetAttributeValuesModal();
    }
    const resetAttributeValuesModal = () => {
        setAttributeValueData(null);
    }

    return (
        <ModalContainer
            isOpen={showRecategorizePopup}
            setOpen={modalRecategorizeClose}
            width="1000px"
            height="85%"
            clickOutSideClose={false}
        >
            <div className="tw-flex tw-flex-col tw-h-full">
                <div className="tw-flex-none">
                    <ModalHeader>
                        {attributeValueData ? <AttributeValuesMappingHeader attributeValuesCount={attributeValueData.attributeValuesCount} onBack={handleAttributeValuesBackButton} onDone={handleDoneButton} /> :
                            <div className='tw-flex tw-justify-between'>
                                <div className="tw-py-2">
                                    <Label
                                        message={RECATEGORIZE_TITLE}
                                        fontSize={"0.875"}
                                        fontWeight="600"
                                    />
                                </div>
                                {
                                    showResult ? null :
                                        <button type="button" className="red-button-outline tw-mr-2" onClick={modalRecategorizeClose} >Close</button>
                                }
                            </div>}
                    </ModalHeader>
                </div>
                {attributeValueData ? <AttributeValuesMapping payload={payload} setPayload={setPayload} recategorizationResponse={recategorizationResponse} attributeValueData={attributeValueData} /> :
                    !showResult ?
                        <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0">
                            <ModalBody>
                                <div className='tw-space-y-3'>
                                    <div className="tw-sticky tw-top-0 tw-z-10 tw-bg-white">
                                        <div className="tw-bg-[#FFFAE6] tw-border tw-my-2 tw-mx-4 tw-border-[#FFC400] tw-flex tw-items-center tw-px-4 tw-py-1">
                                        <WarningIcon />
                                        <p className="tw-ml-2">{WARNING_MESSAGE}</p>
                                        </div>
                                        <Stepper
                                            currentStep={currentStep}
                                            onStepChange={handleStepChange}
                                            steps={getSteps(currentStep)}
                                            payload={payload}
                                        />
                                    </div>
                                    <div>
                                        <div className='tw-flex tw-justify-center tw-items-center tw-mt-4'>
                                                {currentStep === 1 && (
                                                    <SelectTarget
                                                        selectedProducts={selectedProducts}
                                                        setSelectedProducts={setSelectedProducts}
                                                        payload={payload}
                                                        setPayload={setPayload}
                                                    />
                                                )}
                                                {currentStep === 2 && (
                                                    <SelectAttributes
                                                        payload={payload}
                                                        setPayload={setPayload}
                                                        recategorizationResponse={recategorizationResponse}
                                                        setAttributeValueData={setAttributeValueData}
                                                        activeTabIndex={activeTabIndex}
                                                        setActiveTabIndex={setActiveTabIndex}
                                                    />
                                                )}
                                                {currentStep === 3 && <Review
                                                    selectedProducts={selectedProducts}
                                                    setSelectedProducts={setSelectedProducts}
                                                    payload={payload}
                                                    setPayload={setPayload}
                                                    finalPayload={finalPayload}
                                                    setFinalPayload={setFinalPayload}
                                            />}
                                        </div>
                                    </div>
                                </div>
                            </ModalBody>
                        </div>
                        :
                        <div className='tw-flex-1 tw-overflow-y-auto tw-min-h-0'><RecategorizationWrapper setShowResult={setShowResult} modalRecategorizeClose={modalRecategorizeClose} payload={payload} recategorizationResponse={recategorizationResponse as RecategorizationResponse} /></div>}
                            {!attributeValueData && <div className="tw-flex-none tw-border-t tw-bg-white">
                                {!showResult ? (
                                    <ModalFooter>
                                        <div className='tw-flex tw-row tw-justify-between tw-w-full'>
                                            <div>
                                                {currentStep === 3 ? (!loader && <button onClick={handleBack} className="popup-button-outline">Back</button>) 
                                                : (currentStep !== 1 && <button onClick={handleBack} className="popup-button-outline">Back</button>)}
                                            </div>
                                            <div>
                                                {currentStep === 3 ? (
                                                <button onClick={handleSubmit} disabled={loader} className="popup-button-filled">{loader ? "Submitting..." : "Submit"}</button>)
                                                : ( <button onClick={handleNext} disabled={disableNextButton()} className="popup-button-filled">{NEXT}</button>)}
                                            </div>
                                        </div>
                                    </ModalFooter>
                                ) : null}
                            </div>}
                        </div>
        </ModalContainer>
    )
}
export default RecategorizeModal;